import { inject } from '@angular/core';
import { ResolveFn } from '@angular/router';
import { RuntimeConfigService } from '@core/services';
import { PendoService } from '@core/services/pendo/pendo.service';
import { filter, map, take } from 'rxjs/operators';

/**
 * Resolver to initialize Pendo analytics when navigating to protected routes
 *
 * This resolver:
 * 1. Waits for runtime config to be loaded (filters out null)
 * 2. Calls PendoService.initialize() which handles two scenarios:
 *    - If config has Pendo data: initializes with full user/company data
 *    - If no config data but localStorage has IDs: reinitializes from storage
 * 3. Returns true when complete
 *
 * Usage:
 * ```typescript
 * {
 *   path: 'protected',
 *   component: MyComponent,
 *   resolve: {
 *     config: runtimeConfigResolver,
 *     pendo: pendoResolver  // Add after runtimeConfigResolver
 *   }
 * }
 * ```
 *
 * Note: This resolver should be used alongside runtimeConfigResolver,
 * as it depends on the runtime config being loaded first. It waits for
 * the config to be non-null before proceeding.
 */
export const pendoResolver: ResolveFn<boolean> = () => {
  const runtimeConfigService = inject(RuntimeConfigService);
  const pendoService = inject(PendoService);

  return runtimeConfigService.getConfig().pipe(
    filter((config) => config !== null), // Wait for config to be loaded
    take(1), // Only take the first non-null emission
    map((config) => {
      if (config?.pendo) {
        // Initialize with full data from backend
        pendoService.initialize(config.pendo);
      }
      // If no Pendo data in config, user has opted out - do nothing
      return true;
    })
  );
};
